import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { DownloadSimple } from "@phosphor-icons/react";

/**
 * PWA install prompt button.
 * Shows only when the browser has fired `beforeinstallprompt` (i.e. the app
 * meets installability criteria AND is not already installed). Hides after a
 * successful install or dismissal.
 */
export default function PwaInstallButton({ variant = "compact" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // If already running standalone, mark as installed
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
      setInstalled(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const doInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch (e) { /* ignored */ }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  if (installed || !deferredPrompt) return null;

  if (variant === "compact") {
    return (
      <button
        onClick={doInstall}
        className="hidden md:flex items-center gap-1.5 px-2.5 h-8 border border-lime-400/40 text-lime-400 hover:bg-lime-400/10 font-mono-data text-[11px] tracking-wide transition-colors"
        data-testid="btn-pwa-install"
        title="Installa ImmoScope come app"
      >
        <DownloadSimple size={14} weight="bold" />
        INSTALLA APP
      </button>
    );
  }

  return (
    <Button
      onClick={doInstall}
      className="rounded-none bg-lime-400 text-lime-950 hover:bg-lime-500 font-bold font-mono-data text-xs h-11 px-6"
      data-testid="btn-pwa-install"
    >
      <DownloadSimple size={16} weight="bold" className="mr-2" />
      INSTALLA APP
    </Button>
  );
}
